import { PageSize, ROUTERS, TableTypes } from '@/constant';
import { useGetListData } from '@/hooks';
import { DOCUMENT_SEARCH_OPTIONS } from '@/pages/workers/dm1-02/components/dm1-02-1/config';
import {
	CommonActions,
	DefineDocumentAreaActions,
	DefineDocumentAreaSelectors,
	useAppSelector,
	WorkerDocumentActions,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { NOOP, showAlert, showToast } from '@/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DefaultOptionType } from 'antd/es/select';

import { IDefineDocumentAreaProps } from '../define-document-area.type';
import { getColumns } from './config';
export const useLogic = (props: IDefineDocumentAreaProps) => {
	const {
		total,
		list,
		fetching: isLoading,
	} = useGetListData({
		type: TableTypes.DEFINE_DOCUMENT_AREA,
	});
	const { isWorkerPublicAdminRole } = useAppSelector(
		MyPageSelectors.selectDataMyPage
	);
	const selectParams = useAppSelector(DefineDocumentAreaSelectors.selectParams);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { t } = useTranslation(['documentArea', 'validation', 'message']);

	const fetchData = useCallback(
		(params: Partial<typeof selectParams>) => {
			if (props.refTableId)
				dispatch(
					DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
						...selectParams,
						...params,
						pageSize: PageSize.XXXS,
						refTableId: props.refTableId?.toString(),
						refTableName: props.pageType,
					})
				);
		},
		[props.refTableId, props.pageType]
	);

	useEffect(() => {
		dispatch(CommonActions.getAutocompleteSuggestionRequest());
		let timer = setTimeout(() => {
			fetchData({
				page: 1,
			});
		}, 0);

		return () => {
			clearTimeout(timer);
		};
	}, []);

	const columns = useMemo(
		() => getColumns(list, props.pageType),
		[list, props.pageType]
	);

	const handlePageSizeChange = useCallback(
		(value: DefaultOptionType['value']) => {
			fetchData({
				page: Number(value),
			});
		},
		[]
	);

	const handlePageChange = useCallback(
		(page: number) => {
			dispatch(
				DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
					...selectParams,
					page: page,
				})
			);
		},
		[selectParams]
	);

	const handleDeleteDocuments = useCallback(
		(ids: number[], callbackSuccess?: () => void) => {
			if (!ids?.length) {
				showAlert(t('message:no-file-to-delete'));
				return;
			}
			showAlert(
				t('message:ALERT_17', { record: t('files') }),
				() => {
					dispatch(
						DefineDocumentAreaActions.deleteDocumentsRequest({
							params: {
								refTableName: props.pageType,
								refTableId: props.refTableId.toString(),
								history: false, //avatar, companySeal has value true
								id: ids,
							},
							callback: (error) => {
								if (error) {
									showAlert(error?.message);
									return;
								}
								showToast(t('message:DELETE_SUCCESS'));
								fetchData({
									...selectParams,
									page: 1,
								});
								callbackSuccess?.();
							},
						})
					);
				},
				NOOP
			);
		},
		[fetchData, t]
	);

	const navigateRegister = useCallback(() => {
		const labelName =
			DOCUMENT_SEARCH_OPTIONS?.find((item) => item?.type === props.pageType)
				?.label ?? '';
		dispatch(
			WorkerDocumentActions.handleNextStep({
				pageType: props.pageType,
				id: props.refTableId,
				name: props?.zipFileName,
				labelName,
				step: 2,
				pathname: window.location.pathname,
				refTableName: props.pageType,
			})
		);
		navigate(ROUTERS.DM1_02_02);
	}, [window.location.pathname]);

	return {
		columns,
		handlePageSizeChange,
		total,
		handlePageChange,
		isLoading,
		handleDeleteDocuments,
		navigateRegister,
		isWorkerPublicAdminRole,
	};
};
